import { useCallback, useEffect, useRef, useState } from "react";
import { MotiView } from "moti";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { View } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import Sentry from "../../../utils/sentry";
import {
  useGetAssessmentSubmissionsDetailsQuery,
  useGetCourseByIdQuery,
  useMeQuery,
} from "../../../graphql/generated/graphql";
import Skeleton from "react-loading-skeleton";
import AiTutorCardProgressList from "../../../components/lms/AiTutorCardProgressList/AiTutorCardProgressList";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../../navigation/AppNavigator.web";
import { sortResourceItemsOfModules } from "../../../utils/sortResourceItemsOfModules";
import AssessmentCard from "../../../components/customCoursework/AssessmentCard/AssessmentCard";
import { ResourceType, useActiveCourse } from "../../../contexts/ActiveCourseContext";
import React from "react";
import AssessmentGradingModalContainer from "../../customCoursework/TeacherGradingUIComponents/AssessmentGradingModalContainer";
import { GradingData } from "../../../views/GradingCenterView";

const AiTutorCardProgressListContainer = ({ integrationId, courseId, onProgressUpdate }) => {
  const [modulesWithResources, setModulesWithResources] = useState([]);
  const [isAssessmentHovered, setIsAssessmentHovered] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isAssessmentGradingModalVisible, setIsAssessmentGradingModalVisible] = useState(false);
  const [selectedAssessmentSubmissionId, setSelectedAssessmentSubmissionId] = useState(null);

  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  const { setActiveResource } = useActiveCourse();

  const { data: meData } = useMeQuery({});
  const currentUserId = meData?.me?.id;

  const {
    data: course,
    loading,
    refetch,
  } = useGetCourseByIdQuery({
    variables: {
      id: courseId,
      withModules: true,
    },
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
  });

  const { data: assessmentSubmissionDetails, loading: assessmentSubmissionDetailsLoading } =
    useGetAssessmentSubmissionsDetailsQuery({
      variables: {
        assessmentSubmissionId: selectedAssessmentSubmissionId,
      },
      skip: !selectedAssessmentSubmissionId,
    });

  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch])
  );

  useEffect(() => {
    if (course && !loading && currentUserId) {
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }

      const formattedModules = course?.getCourseById.data[0].modules.map((module) => {
        const aiTutors = module.aiTutors.map((aiTutor) => {
          const aiTutorTotalObjectives =
            aiTutor.objectives && aiTutor.objectives.length > 0 ? aiTutor.objectives.length : 0;
          const aiTutorCompletedObjectives =
            aiTutor.objectives && aiTutor.objectives.length > 0
              ? aiTutor.objectives.filter(
                  (objective) => objective.userHasProgressConnection?.edges?.[0]?.properties?.status === "completed"
                ).length
              : 0;
          const isComplete = aiTutorCompletedObjectives > 0 && aiTutorCompletedObjectives === aiTutorTotalObjectives;
          const resourceDurationMinutes = (aiTutorTotalObjectives - aiTutorCompletedObjectives) * 10;

          return {
            ...aiTutor,
            resourceType: "aiTutor",
            details: {
              isComplete,
              totalObjectives: aiTutorTotalObjectives,
              completedObjectives: aiTutorCompletedObjectives,
              resourceDurationMinutes,
            },
          };
        });

        const axioResources = module.axioResources.map((resource) => ({
          ...resource,
          resourceType: "axioResource",
          details: {
            title: resource.name,
            description: resource.description,
            videoUrl: resource.videoUrl,
          },
          files: resource.files,
        }));

        // Process assignments
        const assignments = module.assignments.map((assignment) => ({
          ...assignment,
          resourceType: "assignment",
          details: {
            title: assignment.name,
            description: assignment.description,
            videoUrl: assignment.videoUrl,
          },
          files: assignment.files,
        }));

        const reviews = module.reviews
          .map((review) => {
            const userAiTutor = review.hasAITutors
              ?.filter((aiTutor) => aiTutor.reviewForUsers?.some((user) => user.id === currentUserId))
              ?.sort((a, b) => new Date(b.created || 0).getTime() - new Date(a.created || 0).getTime())?.[0];

            if (!userAiTutor) {
              return null;
            }

            const reviewTotalObjectives =
              userAiTutor.objectives && userAiTutor.objectives.length > 0 ? userAiTutor.objectives.length : 0;

            const reviewCompletedObjectives =
              userAiTutor.objectives && userAiTutor.objectives.length > 0
                ? userAiTutor.objectives.filter(
                    (objective) => objective.userHasProgressConnection?.edges?.[0]?.properties?.status === "completed"
                  ).length
                : 0;
            const isComplete = reviewCompletedObjectives > 0 && reviewCompletedObjectives === reviewTotalObjectives;
            const resourceDurationMinutes = (reviewTotalObjectives - reviewCompletedObjectives) * 10;

            return {
              id: review.id,
              title: `Review for ${review.hasReviewFor?.moduleHasAssessment?.title || "Unknown Module"}'s Assessment`,
              resourceType: "review",
              aiTutorIdOfReview: userAiTutor.id,
              details: {
                isComplete,
                totalObjectives: reviewTotalObjectives,
                completedObjectives: reviewCompletedObjectives,
                resourceDurationMinutes,
              },
            };
          })
          .filter(Boolean);

        // Combine all types of resources
        const allResources = [...aiTutors, ...axioResources, ...assignments, ...reviews];

        const latestAssessmentSubmission =
          module.assessment.submissions && module.assessment.submissions.length > 0
            ? module.assessment.submissions.sort((a, b) => {
                const aDate = new Date(a.created);
                const bDate = new Date(b.created);
                return bDate.getTime() - aDate.getTime();
              })[0]
            : null;

        return {
          ...module,
          resources: sortResourceItemsOfModules(allResources, module.resourceOrder),
          latestAssessmentSubmission,
        };
      });

      const totalObjectivesCount = formattedModules.reduce((acc, module) => {
        return (
          acc +
          module.resources.reduce(
            (acc, resource) =>
              resource.resourceType === "aiTutor" || resource.resourceType === "review"
                ? acc + (resource?.details?.totalObjectives || 0)
                : acc,
            0
          )
        );
      }, 0);

      const completedObjectivesCount = formattedModules.reduce((acc, module) => {
        return (
          acc +
          module.resources.reduce(
            (acc, resource) =>
              resource.resourceType === "aiTutor" || resource.resourceType === "review"
                ? acc + (resource?.details?.completedObjectives || 0)
                : acc,
            0
          )
        );
      }, 0);

      const totalProgress = totalObjectivesCount > 0 ? (completedObjectivesCount / totalObjectivesCount) * 100 : 0;

      const totalDurationMinutes = (totalObjectivesCount - completedObjectivesCount) * 10;

      setModulesWithResources(formattedModules);
      onProgressUpdate(totalProgress, totalDurationMinutes);
    }
  }, [course, loading, isInitialLoad, currentUserId]);

  useEffect(() => {
    const hasAnyPendingEvaluationOrRunningAssessment = modulesWithResources.some(
      (module) =>
        module.latestAssessmentSubmission?.isEvaluationPending === true ||
        module.latestAssessmentSubmission?.status === "RUNNING"
    );

    if (hasAnyPendingEvaluationOrRunningAssessment) {
      pollingIntervalRef.current = setInterval(() => {
        refetch();
      }, 2000);
    } else {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [modulesWithResources, refetch]);

  const handleResourcePress = (id, resourceType, aiTutorIdOfReview = null) => {
    if (resourceType === "aiTutor") {
      setActiveResource({ resourceType: ResourceType.ACTIVITY_INTERACTIVE_LEARNING_EXPERIENCE, resourceId: id });
      navigation.navigate("Custom Course AITutor", {
        resource_type: "aiTutor",
        resource_id: id,
        course_id: courseId,
        integration_id: integrationId,
      });
    } else if (resourceType === "review") {
      setActiveResource({ resourceType: ResourceType.ACTIVITY_REVIEW, resourceId: aiTutorIdOfReview });
      navigation.navigate("Custom Course AITutor", {
        resource_type: "aiTutor",
        resource_id: aiTutorIdOfReview,
        course_id: courseId,
        integration_id: integrationId,
      });
    } else if (resourceType === "assignment") {
      setActiveResource({ resourceType: ResourceType.ACTIVITY_ASSIGNMENT, resourceId: id });
      navigation.navigate("Custom Assignment Student", {
        resource_type: "assignment",
        resource_id: id,
        course_id: courseId,
        integration_id: integrationId,
      });
    } else {
      setActiveResource({ resourceType: ResourceType.ACTIVITY_RESOURCE, resourceId: id });
      const resource = modulesWithResources
        .flatMap((module) => module.resources)
        .find((resource) => resource.id === id);

      if (resource) {
        const params = {
          resource_type: resourceType,
          resource_id: resource.id,
          course_id: courseId,
          integration_id: integrationId,
        };

        navigation.navigate("Custom Resource Student", params);
      } else {
        Sentry.captureException(`Resource with id ${id} not found.`);
      }
    }
  };

  const handleOnHoverIn = (id) => {
    setModulesWithResources((prev) =>
      prev.map((module) => ({
        ...module,
        resources: module.resources.map((resource) =>
          resource.id === id ? { ...resource, isHovered: true } : resource
        ),
      }))
    );
  };

  const handleOnHoverOut = (id) => {
    setModulesWithResources((prev) =>
      prev.map((module) => ({
        ...module,
        resources: module.resources.map((resource) =>
          resource.id === id ? { ...resource, isHovered: false } : resource
        ),
      }))
    );
  };

  const handleGoToAssessmentView = async (assessmentId) => {
    navigation.navigate("Custom Course Assessment", {
      assessment_id: assessmentId,
      course_id: courseId,
      integration_id: integrationId,
    });
  };

  const handleDisplayAssessmentResults = async (assessmentSubmissionId) => {
    setSelectedAssessmentSubmissionId(assessmentSubmissionId);
    setIsAssessmentGradingModalVisible(true);
  };

  const handleOnAssessmentHoverIn = (assessmentSubmissionId) => {
    if (!assessmentSubmissionId) {
      return;
    }

    setIsAssessmentHovered(assessmentSubmissionId);
  };

  const handleOnAssessmentHoverOut = () => {
    setIsAssessmentHovered(null);
  };

  const renderContent = () => {
    if (loading && isInitialLoad) {
      return (
        <ScrollView style={{ flex: 1 }}>
          {[1, 2, 3].map((_, idx) => (
            <View key={idx}>
              <CustomSpacing type="vertical" size="l" />
              <Skeleton height={18} width={60} />
              <CustomSpacing type="vertical" size="s" />
              <Skeleton height={32} width={200} />
              <CustomSpacing type="vertical" size="l" />
              {[1, 2, 3].map((__, idx2) => (
                <Skeleton height={80} style={{ marginBottom: 10 }} key={idx2} />
              ))}
            </View>
          ))}
        </ScrollView>
      );
    }

    return (
      <>
        <ScrollView style={{ flex: 1 }}>
          {modulesWithResources.map((module) => (
            <View key={module.id}>
              <CustomSpacing type="vertical" size="l" />
              <CustomText
                text={"MODULE"}
                size="m"
                style={{ fontFamily: "Inter", fontSize: 12, lineHeight: 18, letterSpacing: "0.06", color: "#667085" }}
                useTranslationText={false}
              />
              <CustomText
                text={module.title}
                size="xs"
                textType="display"
                useTranslationText={false}
                weight="ultraBold"
              />
              <CustomSpacing type="vertical" size="l" />
              <AiTutorCardProgressList
                items={module.resources}
                onPress={(id, aiTutorIdOfReview) => {
                  const resource = module.resources.find((r) => r.id === id);
                  handleResourcePress(id, resource?.resourceType, aiTutorIdOfReview);
                }}
                onHoverIn={handleOnHoverIn}
                onHoverOut={handleOnHoverOut}
              />
              {module?.assessment?.isEnabled && (
                <AssessmentCard
                  onButtonPress={() => handleGoToAssessmentView(module?.assessment.id)}
                  grade={module.latestAssessmentSubmission?.totalPoints}
                  status={module.latestAssessmentSubmission?.status}
                  isEvaluationPending={module.latestAssessmentSubmission?.isEvaluationPending}
                  onPress={() => handleDisplayAssessmentResults(module?.latestAssessmentSubmission?.id)}
                  onHoverIn={() => handleOnAssessmentHoverIn(module.latestAssessmentSubmission?.id)}
                  onHoverOut={() => handleOnAssessmentHoverOut()}
                  isHovered={isAssessmentHovered === module.latestAssessmentSubmission?.id}
                />
              )}
            </View>
          ))}
        </ScrollView>
        <AssessmentGradingModalContainer
          isVisible={isAssessmentGradingModalVisible}
          onClose={() => setIsAssessmentGradingModalVisible(false)}
          isLoading={assessmentSubmissionDetailsLoading}
          gradingData={{
            ...(assessmentSubmissionDetails?.getAssessmentSubmissionDetails?.data as unknown as GradingData),
            type: "Assessment",
          }}
          canEditScore={false}
        />
      </>
    );
  };

  return (
    <MotiView
      from={{ opacity: 0, translateY: 20 }}
      animate={{ opacity: 1, translateY: 0 }}
      delay={1500}
      transition={{
        opacity: { type: "timing", duration: 600 },
        translateY: { type: "timing", duration: 600 },
      }}
      style={{ flex: 1 }}
      aria-live="polite">
      {renderContent()}
    </MotiView>
  );
};

export default AiTutorCardProgressListContainer;
